import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import ChatbotAdmin from "@/pages/ChatbotAdmin";
import ChatbotSettings from "@/pages/ChatbotSettings";
import ChatbotOperatorSettings from "@/pages/ChatbotOperatorSettings";
import { Robot, ChatCircleDots, Wrench } from "@phosphor-icons/react";

/**
 * Pagina hub /admin/chatbot: raccoglie tutti i chatbot in un'unica UI.
 * Tre sezioni selezionabili con segmented control top-level:
 *   • Chatbot Pubblico  (FAQ visitatori sito)
 *   • Chatbot PRO       (Consulente Tecnico professionisti)
 *   • Chatbot Operator  (Assistente produzione tecnica operatori — in sviluppo)
 */
const SECTIONS = [
  { id: "public",   label: "Chatbot Pubblico", icon: Chats,          hint: "FAQ visitatori · Contatti dal sito" },
  { id: "pro",      label: "Agente AI",      icon: Robot,          hint: "Consulente Tecnico per professionisti" },
  { id: "operator", label: "Chatbot Operator", icon: Wrench,         hint: "Assistente produzione per operatori" },
];

// alias for icon (avoid missing import if Chats not exported)
function Chats(props) {
  return <ChatCircleDots {...props} />;
}

export default function ChatbotHub() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initial = ["public", "pro", "operator"].includes(searchParams.get("section"))
    ? searchParams.get("section") : "public";
  const [active, setActive] = useState(initial);

  const setSection = (id) => {
    setActive(id);
    const next = new URLSearchParams(searchParams);
    next.set("section", id);
    setSearchParams(next, { replace: true });
  };

  return (
    <div>
      <div className="mb-6">
        <div className="label-eyebrow">Amministrazione</div>
        <h1 className="font-display text-4xl font-bold tracking-tight mt-2">Chatbot</h1>
        <p className="text-muted-foreground mt-2 max-w-2xl">
          Gestisci tutti i chatbot di Housetag da un unico centro di controllo.
        </p>
      </div>

      {/* Segmented control top-level */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6" role="tablist">
        {SECTIONS.map((s) => {
          const isSel = active === s.id;
          const Icon = s.icon;
          return (
            <button
              key={s.id}
              type="button"
              onClick={() => setSection(s.id)}
              role="tab"
              aria-selected={isSel}
              className={`text-left rounded-xl border-2 p-4 transition-all ${isSel
                ? "border-purple-500 bg-purple-50 shadow-sm"
                : "border-slate-200 bg-white hover:border-purple-300 hover:bg-slate-50"}`}
              data-testid={`chatbot-hub-tab-${s.id}`}
            >
              <div className="flex items-center gap-3">
                <div className={`h-10 w-10 rounded-full flex items-center justify-center ${isSel ? "bg-purple-700 text-white" : "bg-slate-100 text-slate-700"}`}>
                  <Icon size={20} weight="fill" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm truncate">{s.label}</div>
                  <div className="text-xs text-slate-500 truncate">{s.hint}</div>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Sezione attiva */}
      <div className="border-t pt-6" data-testid={`chatbot-hub-section-${active}`}>
        {active === "public" && <ChatbotAdmin embedded />}
        {active === "pro" && <ChatbotSettings embedded />}
        {active === "operator" && <ChatbotOperatorSettings embedded />}
      </div>
    </div>
  );
}
